import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { signupService } from '../../services/signup.service';
import { suppliersService } from '../../services/suppliers.service';
import useAvailabilityCheck from '../../helper/useAvailabilityCheck';
import { COMP_INFO } from '../../config/siteConfig';
import { DARK_LOGO_URL as companyLogo } from '../../config/siteConfig';
import { BRAND_NAME } from '../../constants/brand';
import AuthBrandPanel from '../../components/auth/AuthBrandPanel';
// Add this import at the top (after companyLogo import)
import bgImage from '../../assets/images/bg.jpg';
import PasswordInput from '../../components/common/PasswordInput';
import { BANK_TYPES } from '../../types/signup.types';
import { BANKS } from '../../constants/banks';

const labelStyle = {
    display: "block", marginBottom: 6, fontSize: 11, fontWeight: 600, color: "#6b7280",
    textTransform: "uppercase", letterSpacing: "0.05em"
};
const controlStyle = {
    height: 40, borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, color: "#0f172a", backgroundColor: "#fff"
};
const locationSelectStyles = {
    control: (base, state) => ({ ...base, minHeight: 40, borderRadius: 8, fontSize: 13, borderColor: state.isFocused ? "#0E6A96" : "#d1d5db", boxShadow: "none" }),
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    menu: (base) => ({ ...base, fontSize: 13 })
};
// Dropdown label -> SupplierDocument.doc_type
const IDENTITY_PROOF_DOC_TYPES = {
    "Aadhaar Card": "aadhar_card",
    "Voter ID Card": "voter_id_card",
    "Driving Licence": "driving_licence",
    "Passport": "passport"
};
// field -> the uploaded file that belongs to it
const RELATED_FILE = { panNo: "panCardFile", gstNo: "gstCopyFile", identityProofType: "identityProofFile" };
const MOBILE_REGEX = /^\d{10}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GST_REGEX = /^(0[1-9]|[1-2][0-9]|3[0-9])[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT_NUMBER_REGEX = /^\d{9,18}$/;
const ALLOWED_FILE_TYPES = ["image/png", "image/jpeg", "application/pdf"];
const FILE_ACCEPT = ".png,.jpg,.jpeg,.pdf";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
const errorStyle = { fontSize: 11, color: "#dc2626", marginTop: 4 };
const hintStyle = { fontSize: 11, color: "#6b7280", marginTop: 4 };

// Defined at module scope (not inside Signup) so inputs are not remounted on every keystroke
const Section = ({ icon, title, desc, children }) => (
    <section className="signup-section">
        <div className="d-flex align-items-start gap-3 mb-3">
            <span className="signup-section-icon"><i className={`bi ${icon}`} aria-hidden="true"></i></span>
            <div>
                <h2 className="signup-section-title">{title}</h2>
                {desc && <p className="signup-section-desc">{desc}</p>}
            </div>
        </div>
        <div className="row g-3">{children}</div>
    </section>
);

const Field = ({ id, label, required, hint, error, className = "col-12 col-md-6 col-xl-4", children }) => (
    <div className={className}>
        <label htmlFor={id} style={labelStyle}>
            {label}{required && <span className="text-danger"> *</span>}
        </label>
        {children}
        {error ? <div style={errorStyle}>{error}</div> : hint && <div style={hintStyle}>{hint}</div>}
    </div>
);

const Signup = () => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const availability = useAvailabilityCheck(suppliersService);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [formData, setFormData] = useState({
        title: "Mr.", customCode: "",
        firstName: "", lastName: "", gender: "Male",
        mobile: "", email: "", whatsapp: "",
        agencyName: "", website: "", agencyAddress: "",
        country: "", state: "", city: "", pincode: "",
        accountHolderName: "", accountNumber: "", bankName: "", branchName: "", accountType: "", ifscCode: "",
        panNo: "", panCardFile: null,
        gstNo: "", gstCopyFile: null,
        identityProofType: "", identityProofFile: null,
        communicationConsent: false,
        userId: "", password: "", confirmPassword: ""
    });

    // Only India is offered
    useEffect(() => {
        (async () => {
            const res = await signupService.countries();
            if (!res.success) { toast.error(res.message || "Failed to load countries"); return; }
            const india = (res.resData?.data || []).filter(c => c.name?.toLowerCase() === "india");
            setCountries(india);
            if (india[0]) setFormData(prev => ({ ...prev, country: String(india[0].id) }));
        })();
    }, []);

    useEffect(() => {
        setStates([]);
        if (!formData.country) return;
        (async () => {
            const res = await signupService.states(formData.country);
            if (res.success) setStates(res.resData?.data || []);
            else toast.error(res.message || "Failed to load states");
        })();
    }, [formData.country]);

    useEffect(() => {
        setCities([]);
        if (!formData.state) return;
        (async () => {
            const res = await signupService.cities(formData.state);
            if (res.success) setCities(res.resData?.data || []);
            else toast.error(res.message || "Failed to load cities");
        })();
    }, [formData.state]);

    const handleChange = (e) => {
        const { name, type, checked, files } = e.target;
        let { value } = e.target;
        if (type === "file" && files[0] && !ALLOWED_FILE_TYPES.includes(files[0].type)) {
            toast.error("Only PNG, JPG and PDF files are allowed");
            e.target.value = "";
            return;
        }
        if (name === "panNo") value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
        if (["customCode", "accountHolderName", "branchName"].includes(name)) value = value.toUpperCase();
        if (name === "customCode") value = value.replace(/[^A-Z]/g, "").slice(0, 4);
        if (name === "ifscCode") value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
        if (name === "accountNumber") value = value.replace(/\D/g, "").slice(0, 18);
        if (name === "gstNo") value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
        if (name === "mobile" || name === "whatsapp") value = value.replace(/\D/g, "").slice(0, 10);
        setFormData(prev => {
            const next = { ...prev, [name]: type === "checkbox" ? checked : type === "file" ? files[0] : value };
            if (name === "mobile") {
                next.userId = value;
                if (prev.whatsapp === "" || prev.whatsapp === prev.mobile) next.whatsapp = value;
            }
            // a changed number / proof type invalidates the file chosen for it
            if (RELATED_FILE[name] && value !== prev[name]) next[RELATED_FILE[name]] = null;
            if (name === "title") next.gender = value === "Mr." ? "Male" : "Female";
            if (name === "country") { next.state = ""; next.city = ""; }
            if (name === "state") next.city = "";
            return next;
        });
        // file inputs are uncontrolled, so the chosen file must be cleared in the DOM too
        if (RELATED_FILE[name] && value !== formData[name]) {
            const fileInput = document.getElementById(RELATED_FILE[name]);
            if (fileInput) fileInput.value = "";
        }
        if (name === "mobile") availability.check("mobile", "phone", value, { valid: MOBILE_REGEX.test(value), message: "Mobile already exists" });
        if (name === "email") availability.check("email", "email", value.trim(), { valid: EMAIL_REGEX.test(value.trim()), message: "Email already exists" });
        if (name === "customCode") availability.check("customCode", "custom_code", value.trim(), { message: "Supplier code already exists" });
    };

    const errors = {
        mobile: formData.mobile && !MOBILE_REGEX.test(formData.mobile) ? "Enter a 10 digit mobile number" : availability.taken.mobile || "",
        whatsapp: formData.whatsapp && !MOBILE_REGEX.test(formData.whatsapp) ? "Enter a 10 digit WhatsApp number" : "",
        panNo: formData.panNo && !PAN_REGEX.test(formData.panNo) ? "Enter a valid PAN (e.g. ABCDE1234F)" : "",
        accountNumber: formData.accountNumber && !ACCOUNT_NUMBER_REGEX.test(formData.accountNumber) ? "Enter a valid account number (9-18 digits)" : "",
        ifscCode: formData.ifscCode && !IFSC_REGEX.test(formData.ifscCode) ? "Enter a valid IFSC code (e.g. SBIN0001234)" : "",
        gstNo: formData.gstNo && !GST_REGEX.test(formData.gstNo) ? "Enter a valid 15 character GST number (e.g. 22AAAAA0000A1Z5)" : "",
        email: formData.email && !EMAIL_REGEX.test(formData.email) ? "Enter a valid email address" : availability.taken.email || "",
        customCode: availability.taken.customCode || "",
        password: formData.password && !PASSWORD_REGEX.test(formData.password)
            ? "Min 8 characters with letters, numbers and a special character" : "",
        confirmPassword: formData.confirmPassword && formData.password !== formData.confirmPassword
            ? "Passwords do not match" : ""
    };
    const hasErrors = Object.values(errors).some(Boolean);

    const requiredFilled = ["firstName", "lastName", "gender", "customCode", "country", "state", "city", "pincode", "mobile", "email", "agencyName", "agencyAddress",
        "accountHolderName", "accountNumber", "bankName", "branchName", "accountType", "ifscCode",
        "panNo", "panCardFile", "identityProofType", "identityProofFile", "userId", "password", "confirmPassword"]
        .every(key => {
            const v = formData[key];
            return typeof v === "string" ? v.trim() !== "" : !!v;
        }) && formData.communicationConsent && !hasErrors && !availability.isChecking;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!requiredFilled || submitting) return;
        const confirm = await Swal.fire({
            title: "Create account?",
            text: "Please confirm that the details you entered are correct.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, create account",
            cancelButtonText: "Cancel"
        });
        if (!confirm.isConfirmed) return;
        setSubmitting(true);
        try {
            const docs = [];
            const uploads = [
                ["pan_card", formData.panCardFile, formData.panNo],
                ["gst_certificate", formData.gstCopyFile, formData.gstNo],
                [IDENTITY_PROOF_DOC_TYPES[formData.identityProofType], formData.identityProofFile, ""]
            ];
            for (const [docType, file, docNumber] of uploads) {
                if (!file) continue;
                const up = await signupService.uploadFile(file);
                const path = up.resData?.filesUploaded?.[0]?.path;
                if (!up.success || !path) { toast.error(up.message || `Failed to upload ${docType} document`); return; }
                docs.push({ doc_type: docType, doc_url: path, doc_name: file.name, doc_number: docNumber.toUpperCase() });
            }

            const res = await signupService.createSupplier({
                custom_code: formData.customCode.trim(),
                title: formData.title,
                first_name: formData.firstName.trim(),
                last_name: formData.lastName.trim(),
                gender: formData.gender,
                mobile: formData.mobile,
                email: formData.email.trim(),
                whatsapp_number: formData.whatsapp,
                website_url: formData.website.trim(),
                agency_name: formData.agencyName.trim(),
                address: formData.agencyAddress.trim(),
                country_id: formData.country,
                state_id: formData.state,
                city_id: formData.city,
                pincode: formData.pincode.trim(),
                password: formData.password,
                status: "inactive",
                inventory_status: "inactive",
                supplier_docs: docs,
                bank_details: [{
                    account_holder_name: formData.accountHolderName.trim(),
                    account_number: formData.accountNumber,
                    bank_name: formData.bankName.trim(),
                    branch_name: formData.branchName.trim(),
                    account_type: formData.accountType,
                    ifsc_code: formData.ifscCode,
                    is_primary: true
                }]
            });
            if (res.success) {
                toast.success(res.message || "Account created successfully");
                navigate("/");
            } else {
                toast.error(res.message || "Failed to create account");
            }
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    /* ---------- small render helpers ---------- */
    const textInput = (name, placeholder, extra = {}) => (
        <input type="text" className="form-control" id={name} name={name} placeholder={placeholder}
            value={formData[name]} onChange={handleChange} {...extra} style={{ ...controlStyle, ...(extra.style || {}) }} />
    );
    const selectInput = (name, options, required = false, disabled = false) => (
        <select id={name} name={name} value={formData[name]} onChange={handleChange} className="form-select" required={required} disabled={disabled} style={controlStyle}>
            {options}
        </select>
    );

    // Searchable dropdown; items are { id, name } from the location APIs
    const searchSelect = (name, items, placeholder, disabled = false) => {
        const options = items.map(i => ({ value: String(i.id), label: i.name }));
        return (
            <Select inputId={name} name={name} options={options} placeholder={placeholder} isDisabled={disabled}
                value={options.find(o => o.value === formData[name]) || null}
                onChange={opt => handleChange({ target: { name, value: opt ? opt.value : "", type: "select" } })}
                isClearable={name !== "country"} menuPortalTarget={document.body} styles={locationSelectStyles} />
        );
    };

    return (
        <div className="auth_login signup-page" style={{
            position: "fixed",
            inset: 0,
            overflowY: "auto",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "40px 20px",
            backgroundImage: `linear-gradient(rgba(10, 53, 80, 0.45), rgba(10, 53, 80, 0.45)), url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat"
        }}>
            <style>{`
                .signup-card { width: 100%; max-width: 1200px; display: grid; grid-template-columns: 1fr; margin-bottom: 40px;
                               background: #fff; border-radius: 16px; overflow: hidden; overflow: clip; box-shadow: 0 30px 80px rgba(10, 53, 80, .45); }
                @media (min-width: 992px) { .signup-card { grid-template-columns: 360px 1fr; } }
                .signup-brand { background: linear-gradient(160deg, #0b2a2e 0%, #14524f 100%); }
                .signup-brand .auth-brand { background: transparent; position: sticky; top: 0; height: 100%; max-height: calc(100vh - 80px); }
                .signup-main { padding: 40px 48px; min-width: 0; }
                .signup-title { margin: 0; font-size: 22px; font-weight: 700; color: #0f172a; }
                .signup-lead { margin: 4px 0 0; font-size: 13px; color: #64748b; }
                .signup-section { padding: 24px 0; border-top: 1px solid #e5e7eb; }
                .signup-section-icon { width: 32px; height: 32px; flex-shrink: 0; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center;
                                       background: #ecfdf5; color: var(--btn-bg); font-size: 15px; }
                .signup-section-title { margin: 0; font-size: 15px; font-weight: 600; color: #0f172a; line-height: 32px; }
                .signup-section-desc { margin: -4px 0 0; font-size: 12px; color: #6b7280; }
                .signup-check { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid #e5e7eb; border-radius: 8px; background: #f8fafc; }
                .signup-check .form-check-input { width: 18px; height: 18px; margin: 0; cursor: pointer; }
                .signup-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; padding-top: 24px; border-top: 1px solid #e5e7eb; }
                .signup-submit { height: 44px; min-width: 200px; padding: 0 24px; border: 0; border-radius: 8px; background: var(--btn-bg); color: #fff;
                                 font-size: 14px; font-weight: 600; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
                                 box-shadow: 0 4px 14px rgba(14, 106, 150, .25); transition: background-color .15s; }
                .signup-submit:hover:not(:disabled) { background: var(--btn-hover-bg); }
                .signup-submit:disabled { opacity: .5; cursor: not-allowed; box-shadow: none; }
                .signup-submit:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(14, 106, 150, .35); }
                .signup-mobile-brand { display: inline-flex; align-items: center; gap: 10px; margin-bottom: 20px; text-decoration: none; }
                .signup-mobile-brand img { height: 32px; width: auto; }
                .signup-mobile-name { display: block; font-size: 18px; font-weight: 700; line-height: 1.1; color: #0f172a; }
                .signup-mobile-sub { display: block; font-size: 12px; color: #64748b; margin-top: 2px; }
                @media (max-width: 767.98px) {
                    .signup-page { padding: 16px 12px !important; }
                    .signup-main { padding: 28px 20px; }
                    .signup-submit { width: 100%; }
                    .signup-page input, .signup-page select, .signup-page textarea { font-size: 16px !important; }
                }
            `}</style>

            <div className="signup-card">
                {/* Brand panel — desktop only */}
                <div className="signup-brand d-none d-lg-block">
                    <AuthBrandPanel ctaText="Already registered?" ctaLabel="Back to login" ctaTo="/login" />
                </div>

                <div className="signup-main">
                    {/* Header */}
                    <div className="mb-4">
                        <Link to="/" className="signup-mobile-brand d-lg-none" aria-label={`${BRAND_NAME} home`}>
                            <img src={companyLogo} alt="" />
                            <span>
                                <span className="signup-mobile-name">{BRAND_NAME}</span>
                                <span className="signup-mobile-sub">{COMP_INFO.NAME}</span>
                            </span>
                        </Link>
                        <h1 className="signup-title">Create your supplier account</h1>
                        <p className="signup-lead">Register your agency on {COMP_INFO.NAME}. Fields marked <span className="text-danger">*</span> are required.</p>
                    </div>

                    <form onSubmit={handleSubmit}>

                        <Section icon="bi-person" title="Personal Details" desc="The primary contact for this agency">
                            <Field id="title" label="Title" className="col-4 col-md-2">
                                {selectInput("title", ["Mr.", "Mrs.", "Ms."].map(t => <option key={t}>{t}</option>))}
                            </Field>
                            <Field id="firstName" label="First Name" required className="col-8 col-md-5">
                                {textInput("firstName", "First name", { required: true })}
                            </Field>
                            <Field id="lastName" label="Last Name" required className="col-12 col-md-5">
                                {textInput("lastName", "Last name", { required: true })}
                            </Field>
                            <Field id="gender" label="Gender" required>
                                {selectInput("gender", [
                                    <option key="" value="">Select gender</option>,
                                    ...["Male", "Female", "Other"].map(gender => <option key={gender} value={gender}>{gender}</option>)
                                ], true)}
                            </Field>                            
                            <Field id="mobile" label="Mobile No." required hint="This number will be your User ID" error={errors.mobile}>
                                <div className="input-group">
                                    <span className="input-group-text" style={{ height: 40, borderRadius: "8px 0 0 8px", border: "1px solid #d1d5db", background: "#f8fafc", fontSize: 13, fontWeight: 600, color: "#374151" }}>+91</span>
                                    <input type="tel" inputMode="numeric" maxLength={10} className="form-control" id="mobile" name="mobile" placeholder="10 digit mobile number" value={formData.mobile} onChange={handleChange} required
                                        style={{ ...controlStyle, borderRadius: "0 8px 8px 0" }} />
                                </div>
                            </Field>
                            <Field id="email" label="Email" required error={errors.email}>
                                <input type="email" className="form-control" id="email" name="email" placeholder="name@agency.com" value={formData.email} onChange={handleChange} required style={controlStyle} />
                            </Field>
                            <Field id="whatsapp" label="WhatsApp No." error={errors.whatsapp}>
                                <input type="tel" inputMode="numeric" maxLength={10} className="form-control" id="whatsapp" name="whatsapp" placeholder="10 digit WhatsApp number" value={formData.whatsapp} onChange={handleChange} style={controlStyle} />
                            </Field>
                            <Field id="customCode" label="Supplier Code (up to 4 char)" required error={errors.customCode}>
                                {textInput("customCode", "Supplier code", { required: true, maxLength: 4, style: { textTransform: "uppercase" } })}
                            </Field>
                        </Section>

                        <Section icon="bi-building" title="Agency & Address" desc="Where your agency is registered">
                            <Field id="agencyName" label="Agency Name" required className="col-12 col-md-6">
                                {textInput("agencyName", "Agency name", { required: true })}
                            </Field>
                            <Field id="website" label="Website" className="col-12 col-md-6">
                                {textInput("website", "https://www.agency.com")}
                            </Field>
                            <Field id="agencyAddress" label="Agency Address" required className="col-12">
                                {textInput("agencyAddress", "Building, street and area", { required: true })}
                            </Field>
                            <Field id="country" label="Country" required className="col-6 col-md-3">
                                {searchSelect("country", countries, "Select country")}
                            </Field>
                            <Field id="state" label="State" required className="col-6 col-md-3">
                                {searchSelect("state", states, "Select state", !formData.country)}
                            </Field>
                            <Field id="city" label="City" required className="col-6 col-md-3">
                                {searchSelect("city", cities, "Select city", !formData.state)}
                            </Field>
                            <Field id="pincode" label="Pin Code" required className="col-6 col-md-3">
                                {textInput("pincode", "Pin code", { required: true })}
                            </Field>
                        </Section>

                        <Section icon="bi-bank" title="Bank Details" desc="Account used for your payouts">
                            <Field id="accountHolderName" label="Account Holder Name" required className="col-12 col-md-6">
                                {textInput("accountHolderName", "Name as per bank records", { required: true, style: { textTransform: "uppercase" } })}
                            </Field>
                            <Field id="accountNumber" label="Account Number" required error={errors.accountNumber} className="col-12 col-md-6">
                                {textInput("accountNumber", "Bank account number", { required: true, inputMode: "numeric" })}
                            </Field>
                            <Field id="bankName" label="Bank Name" required className="col-12 col-md-6">
                                {searchSelect("bankName", BANKS.filter(b => b.isShow).map(b => ({ id: b.name, name: b.name })), "Select bank")}
                            </Field>
                            <Field id="branchName" label="Branch Name" required className="col-12 col-md-6">
                                {textInput("branchName", "Branch name", { required: true, style: { textTransform: "uppercase" } })}
                            </Field>
                            <Field id="accountType" label="Account Type" required className="col-12 col-md-6">
                                {selectInput("accountType", [
                                    <option key="" value="">Select account type</option>,
                                    ...BANK_TYPES.map(type => <option key={type} value={type} style={{ textTransform: "capitalize" }}>{type}</option>)
                                ], true)}
                            </Field>
                            <Field id="ifscCode" label="IFSC Code" required error={errors.ifscCode} className="col-12 col-md-6">
                                {textInput("ifscCode", "SBIN0001234", { required: true, style: { textTransform: "uppercase" } })}
                            </Field>
                        </Section>

                        <Section icon="bi-file-earmark-text" title="Tax & Documents" desc="PAN and address or identity verification">
                            <Field id="panNo" label="PAN Card Number" required error={errors.panNo} className="col-12 col-md-6">
                                {textInput("panNo", "ABCDE1234F", { required: true, style: { textTransform: "uppercase" } })}
                            </Field>
                            <Field id="panCardFile" label="PAN Card Image" required hint="Upload PNG, JPG or PDF of your PAN card" className="col-12 col-md-6">
                                <input type="file" className="form-control" id="panCardFile" name="panCardFile" accept={FILE_ACCEPT} onChange={handleChange} required style={controlStyle} />
                            </Field>
                            <Field id="gstNo" label="GST Number" error={errors.gstNo} className="col-12 col-md-6">
                                {textInput("gstNo", "22AAAAA0000A1Z5", { style: { textTransform: "uppercase" } })}
                            </Field>
                            <Field id="gstCopyFile" label="GST Copy Image" hint="Optional — PNG, JPG or PDF of your GST certificate" className="col-12 col-md-6">
                                <input type="file" className="form-control" id="gstCopyFile" name="gstCopyFile" accept={FILE_ACCEPT} onChange={handleChange} style={controlStyle} />
                            </Field>
                            <Field id="identityProofType" label="Address / Identity Proof" required className="col-12 col-md-6">
                                {selectInput("identityProofType", [
                                    <option key="" value="">Select proof type</option>,
                                    ...Object.keys(IDENTITY_PROOF_DOC_TYPES).map(proof => <option key={proof} value={proof}>{proof}</option>)
                                ], true)}
                            </Field>
                            <Field id="identityProofFile" label="Address / Identity Proof Copy Image" required hint="Upload PNG, JPG or PDF of your selected proof" className="col-12 col-md-6">
                                <input type="file" className="form-control" id="identityProofFile" name="identityProofFile" accept={FILE_ACCEPT} onChange={handleChange} required style={controlStyle} />
                            </Field>
                        </Section>

                        <Section icon="bi-shield-lock" title="Login Credentials" desc="You will use these to sign in">
                            <Field id="userId" label="User ID" required>
                                {textInput("userId", "Auto-filled from mobile number", { required: true, disabled: true })}
                            </Field>
                            <Field id="password" label="Password" required error={errors.password}>
                                <PasswordInput className="form-control" id="password" name="password" placeholder="Password" value={formData.password} onChange={handleChange} required style={controlStyle} />
                            </Field>
                            <Field id="confirmPassword" label="Confirm Password" required error={errors.confirmPassword}>
                                <PasswordInput className="form-control" id="confirmPassword" name="confirmPassword" placeholder="Re-enter password" value={formData.confirmPassword} onChange={handleChange} required style={controlStyle} />
                            </Field>
                        </Section>

                        <div className="mb-4">
                            <label className="signup-check align-items-start" htmlFor="communicationConsent" style={{ cursor: "pointer", marginBottom: 0 }}>
                                <input
                                    type="checkbox"
                                    className="form-check-input flex-shrink-0"
                                    id="communicationConsent"
                                    name="communicationConsent"
                                    checked={formData.communicationConsent}
                                    onChange={handleChange}
                                    required
                                />
                                <span style={{ fontSize: 13, fontWeight: 500, color: "#374151", lineHeight: 1.6 }}>
                                    I authorize VirtualToActual and its representatives to contact me via call, SMS, and email regarding my travel/business enquiry. You can manage communication preferences in accordance with applicable policies. Read our{" "}
                                    <a href="https://series-fare.virtualtoactual.cloud/legal/terms-and-conditions" target="_blank" rel="noreferrer">Terms &amp; Conditions</a>
                                    {" "}and{" "}
                                    <a href="https://series-fare.virtualtoactual.cloud/legal/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                                </span>
                            </label>
                        </div>

                        <div className="signup-footer">
                            <span style={{ fontSize: 13, color: "#64748b" }}>
                                Already have an account?{" "}
                                <Link to="/login" style={{ fontWeight: 600, color: "var(--btn-bg)", textDecoration: "none" }}>Log in</Link>
                            </span>
                            <button type="submit" className="signup-submit" disabled={!requiredFilled || submitting}>
                                <i className="bi bi-person-check" aria-hidden="true"></i> {submitting ? "Creating..." : "Create Account"}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
};

export default Signup;
